import type { DocPage } from '../types'
import { media } from '../media'

export const styles: DocPage = {
  id: 'styles',
  title: 'Styles import & export',
  description:
    'Import CAD, KML, and Smallworld symbology into the transfer pipeline and export QML, SLD, XStyles, or database layer_styles after transfer.',
  category: 'Formats',
  sections: [
    { id: 'overview', title: 'Overview' },
    { id: 'cad-import', title: 'CAD style import' },
    { id: 'kml-import', title: 'KML style import' },
    { id: 'sw-import', title: 'Smallworld style import' },
    { id: 'export', title: 'Layer style export' },
    { id: 'export-fields', title: 'Export options table' },
  ],
  blocks: [
    {
      type: 'heading',
      level: 2,
      id: 'overview',
      text: 'Overview',
    },
    {
      type: 'paragraph',
      text: 'Style import runs on supported Source formats (notably AutoCAD, KML, Smallworld). Style export runs after transfer when the Target form enables Layer style files (after transfer) — commonly GeoPackage and PostGIS.',
    },
    {
      type: 'heading',
      level: 2,
      id: 'cad-import',
      text: 'CAD style import',
    },
    {
      type: 'table',
      headers: ['Option', 'Description'],
      rows: [
        ['Resolve BYLAYER / BYBLOCK', 'Expand inherited CAD properties into concrete style values.'],
        ['Import text labels', 'Include text entities in the label/style pipeline.'],
        ['Import hatch/fills', 'Include hatch and fill symbology.'],
        ['Import block symbols', 'Include block symbol definitions.'],
        ['Build shared style library', 'Maintain a reusable shared library when configured.'],
        ['Basic', 'Lowest fidelity import.'],
        ['Standard', 'Balanced fidelity.'],
        ['High Fidelity', 'Maximum detail for High quality imports.'],
      ],
    },
    {
      type: 'heading',
      level: 2,
      id: 'kml-import',
      text: 'KML style import',
    },
    {
      type: 'paragraph',
      text: 'Optional KML style import maps Style / StyleMap definitions into symbolizers used by the transfer/style pipeline. Enable it on the KML source form when you need symbology preserved toward QML/SLD export targets.',
    },
    {
      type: 'heading',
      level: 2,
      id: 'sw-import',
      text: 'Smallworld style import',
    },
    {
      type: 'table',
      headers: ['Option', 'Description'],
      rows: [
        ['Import Smallworld styles', 'Master enable for Smallworld symbology import.'],
        ['Display style / scale', 'Context for which display style to read.'],
        ['Style group / drawing application', 'Style group selection.'],
      ],
    },
    {
      type: 'heading',
      level: 2,
      id: 'export',
      text: 'Layer style export',
    },
    {
      type: 'paragraph',
      text: 'After transfer (when the target form enables Layer style export), export styles as:',
    },
    {
      type: 'figure',
      src: media.styleExportAccordion,
      alt: 'Layer style export accordion',
      caption:
        'Figure: Layer style export options — sidecars (QML / SLD / XStyles), Write QGIS layer_styles in DB, output folder, and geometry type.',
      size: 'compact',
    },
    {
      type: 'table',
      monoFirst: true,
      headers: ['Format', 'Typical consumer'],
      rows: [
        ['QML (QGIS)', 'QGIS layer style files'],
        ['SLD (OGC)', 'OGC Styled Layer Descriptor'],
        ['XStyles JSON', 'SEPM style interchange'],
        ['layer_styles', 'QGIS styles table inside GeoPackage or PostGIS'],
      ],
    },
    {
      type: 'heading',
      level: 2,
      id: 'export-fields',
      text: 'Export options table',
    },
    {
      type: 'table',
      headers: ['Option', 'Description'],
      rows: [
        ['Export style sidecars', 'Toggle writing filesystem style files.'],
        ['QML (QGIS)', 'Enable QML output.'],
        ['SLD (OGC)', 'Enable SLD output.'],
        ['XStyles JSON', 'Enable XStyles JSON output.'],
        ['Extra QML/SLD per style definition', 'Additional files per definition.'],
        ['per target table', 'Also emit per target table when enabled.'],
        ['Write QGIS layer_styles in DB', 'Write into PostGIS/GeoPackage layer_styles.'],
        ['layer_styles schema', 'Schema for the layer_styles table (PostGIS).'],
        ['Output folder', 'Destination folder for sidecars.'],
        ['Layer base name', 'Base name for generated files.'],
        ['Geometry type (symbolizer)', 'Geometry type used when choosing symbolizers.'],
      ],
    },
    {
      type: 'callout',
      variant: 'info',
      title: 'Related forms',
      text: 'PostGIS documents the same accordion in context of the database connection. GeoPackage export writes layer_styles into the .gpkg when enabled.',
    },
  ],
}
